import React, { useState, useEffect } from 'react';
import { FaTimes, FaBell, FaBellSlash } from 'react-icons/fa';
import { useTheme } from '@theme/useTheme';
import LoginSignUp from '@components/forms/LoginSignUp';

interface AutoLoginModalProps {
  visible: boolean;
  onClose: () => void;
  onDisableAutoLogin: () => void;
}

const AutoLoginModal: React.FC<AutoLoginModalProps> = ({
  visible,
  onClose,
  onDisableAutoLogin
}) => {
  const { colors } = useTheme();
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [visible]);

  if (!visible) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDisableAutoLogin = () => {
    onDisableAutoLogin();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden"
      style={{ backgroundColor: 'rgba(26, 34, 48, 0.8)' }}
      onClick={handleOverlayClick}
    >
      <div
        className="w-full max-w-md shadow-2xl overflow-hidden relative border my-6 max-h-[90vh] flex flex-col"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
          borderTopRightRadius: '2rem',
          borderBottomLeftRadius: '2rem'
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full transition-colors duration-200 z-10"
          style={{
            color: colors.error,
            backgroundColor: colors.error + '20'
          }}
          aria-label="Close modal"
        >
          <FaTimes size={20} />
        </button>

        {/* Auto-login notification banner */}
        <div
          className="px-6 py-3 border-b flex-shrink-0"
          style={{ backgroundColor: colors.secondaryButton }}
        >
          <div className="flex items-center space-x-3">
            <FaBell
              size={16}
              style={{ color: colors.primary }}
            />
            <div>
              <p
                className="text-sm font-semibold"
                style={{ color: colors.heading }}
              >
                Welcome to Sleep Apnea Assessment
              </p>
              <p
                className="text-xs"
                style={{ color: colors.text }}
              >
                Sign in to save your progress and access personalized results
              </p>
            </div>
          </div>
        </div>


        {/* Login/Signup Form - Scrollable Content */}
        <div className="px-8 pt-6 pb-8 overflow-y-auto flex-auto">
          <LoginSignUp
            closeModal={onClose}
            errors={errors}
            setErrors={setErrors}
          />

          {/* Error Display */}
          {errors.length > 0 && (
            <div
              className="mt-4 p-3 border rounded-xl"
              style={{
                backgroundColor: colors.error + '20',
                borderColor: colors.error
              }}
            >
              {errors.map((error, idx) => (
                <p key={idx} className="text-sm text-center" style={{ color: colors.error }}>
                  {error}
                </p>
              ))}
            </div>
          )}


          {/* Disable Auto-login Option */}
          <div
            className="mt-6 pt-4 border-t"
            style={{ borderColor: colors.border }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FaBellSlash
                  size={14}
                  style={{ color: colors.text }}
                />
                <span
                  className="text-sm"
                  style={{ color: colors.text }}
                >
                  Don't show this again
                </span>
              </div>
              <button
                onClick={handleDisableAutoLogin}
                className="text-sm font-semibold hover:underline px-3 py-1 rounded-lg transition-colors duration-200"
                style={{
                  color: colors.primary,
                  backgroundColor: colors.secondaryButton
                }}
              >
                Disable
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoLoginModal;
